/**
 * Typed wrappers over apiClient for the /api/users admin endpoints.
 * Mirrors apps/admin-back/src/routes/UsersRouter.ts.
 */
import { apiClient } from '@/lib/apiClient';
import type {
  HeatmapDay,
  PointHistoryEntry,
  UserActionResponse,
  UserCheckin,
  UserDetail,
  UserGoal,
  UserListResponse,
  UserPhase,
  UserTask,
} from '../types';

export interface ListUsersParams {
  limit?: number;
  cursor?: string;
  search?: string;
  isActive?: boolean;
}

export interface CheckinsListParams {
  limit?: number;
  fromDate?: string;
  toDate?: string;
}

export interface AdjustXpBody {
  delta: number;
  reason: string;
}

export interface ToggleHolidayBody {
  enable: boolean;
  until?: string;
}

export interface GrantAccessoryBody {
  accessoryId: string;
  action: 'grant' | 'revoke';
}

export interface SoftDeleteBody {
  reason: string;
}

export interface SendNotificationBody {
  title: string;
  body: string;
  notificationType: string;
  category: string;
  data?: Record<string, string>;
  targetRoute?: string;
}

export interface BypassPrerequisiteBody {
  reason: string;
}

export interface ForceEnqueueBody {
  goalCategories: string[];
}

export const usersApi = {
  list: (params: ListUsersParams = {}) =>
    apiClient.get<UserListResponse>('/users', { ...params }),

  detail: (userId: string) =>
    apiClient.get<UserDetail>(`/users/${encodeURIComponent(userId)}`),

  goals: (userId: string) =>
    apiClient.get<{ goals: UserGoal[] }>(`/users/${encodeURIComponent(userId)}/goals`),

  goalPhases: (userId: string, goalId: string) =>
    apiClient.get<{ phases: UserPhase[] }>(
      `/users/${encodeURIComponent(userId)}/goals/${encodeURIComponent(goalId)}/phases`,
    ),

  phaseTasks: (userId: string, goalId: string, phaseId: string) =>
    apiClient.get<{ tasks: UserTask[] }>(
      `/users/${encodeURIComponent(userId)}/goals/${encodeURIComponent(goalId)}/phases/${encodeURIComponent(phaseId)}/tasks`,
    ),

  accessories: (userId: string) =>
    apiClient.get<{ ownedAccessoryIds: string[]; claimableAccessoryIds: string[] }>(
      `/users/${encodeURIComponent(userId)}/accessories`,
    ),

  notificationState: (userId: string) =>
    apiClient.get<{ notificationState: unknown }>(
      `/users/${encodeURIComponent(userId)}/notification-state`,
    ),

  checkins: (userId: string, params: CheckinsListParams = {}) =>
    apiClient.get<{ checkins: UserCheckin[] }>(
      `/users/${encodeURIComponent(userId)}/checkins`,
      { ...params },
    ),

  pointsHistory: (userId: string, limit?: number) =>
    apiClient.get<{ history: PointHistoryEntry[] }>(
      `/users/${encodeURIComponent(userId)}/points-history`,
      { limit },
    ),

  checkinHeatmap: (userId: string, days?: number) =>
    apiClient.get<{ days: HeatmapDay[] }>(
      `/users/${encodeURIComponent(userId)}/checkins/heatmap`,
      { days },
    ),

  // ── Actions ──
  adjustXp: (userId: string, body: AdjustXpBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/xp`,
      body,
    ),

  toggleHoliday: (userId: string, body: ToggleHolidayBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/holiday`,
      body,
    ),

  grantAccessory: (userId: string, body: GrantAccessoryBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/accessory`,
      body,
    ),

  softDelete: (userId: string, body: SoftDeleteBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/soft-delete`,
      body,
    ),

  sendNotification: (userId: string, body: SendNotificationBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/notify`,
      body,
    ),

  bypassPrerequisite: (userId: string, body: BypassPrerequisiteBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/bypass-prerequisite`,
      body,
    ),

  forceEnqueue: (userId: string, body: ForceEnqueueBody) =>
    apiClient.post<UserActionResponse>(
      `/users/${encodeURIComponent(userId)}/actions/force-enqueue`,
      body,
    ),
};
